/**
 * RegulationDisplay - Shows structured regulation data
 *
 * Displays regulations categorized as:
 * - Primary: Core regulations directly applicable
 * - Secondary: Supporting regulations
 * - Industry: Industry-specific or regional regulations
 */
import React, { useState } from 'react';
import { Collapse, Tag, Tooltip, Badge } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Panel } = Collapse;

const SEVERITY_COLORS = {
  critical: 'red',
  high: 'orange',
  medium: 'blue',
  low: 'green'
};

const CATEGORY_LABELS = {
  primary: { label: 'Primary Regulations', color: '#8B3C46', icon: '⭐' },
  secondary: { label: 'Supporting Regulations', color: '#4A5568', icon: '🔗' },
  industry: { label: 'Industry & Regional', color: '#2C5282', icon: '🏢' }
};

function RegulationDisplay({ regulationData, legacyRegulation }) {
  const [activeKeys, setActiveKeys] = useState(['primary']); // Expand primary by default

  // If no structured data, fall back to legacy format
  if (!regulationData || !regulationData.regulations) {
    return (
      <div>
        <p style={{ color: '#666' }}>{legacyRegulation || 'No regulation data available'}</p>
      </div>
    );
  }

  const { regulations } = regulationData;

  // Group by category
  const grouped = regulations.reduce((acc, reg) => {
    const category = reg.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(reg);
    return acc;
  }, {});

  return (
    <div style={{ marginTop: '16px' }}>
      <Collapse
        activeKey={activeKeys}
        onChange={setActiveKeys}
        style={{ background: '#fff' }}
        bordered={false}
      >
        {Object.entries(grouped).map(([category, regs]) => {
          const categoryInfo = CATEGORY_LABELS[category] || { label: category, color: '#666', icon: '📋' };

          return (
            <Panel
              key={category}
              header={
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '16px' }}>{categoryInfo.icon}</span>
                  <strong style={{ color: categoryInfo.color }}>{categoryInfo.label}</strong>
                  <Badge count={regs.length} style={{ backgroundColor: categoryInfo.color }} />
                </div>
              }
              style={{ marginBottom: '8px', border: `1px solid ${categoryInfo.color}33` }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {regs.map((reg, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <Tag color="blue" style={{ margin: 0, fontWeight: 'bold' }}>
                            {reg.framework}
                          </Tag>
                          <span style={{ fontWeight: '600', color: '#111827' }}>
                            {reg.reference}
                          </span>
                          <Tag color={SEVERITY_COLORS[reg.severity] || 'default'} style={{ margin: 0 }}>
                            {reg.severity}
                          </Tag>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: '500', color: '#374151', marginTop: '6px' }}>
                          {reg.title}
                        </div>
                      </div>
                      {reg.description && (
                        <Tooltip title={reg.description} placement="left">
                          <InfoCircleOutlined style={{ color: '#6b7280', cursor: 'help', fontSize: '16px' }} />
                        </Tooltip>
                      )}
                    </div>
                    {reg.description && (
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px', lineHeight: '1.5' }}>
                        {reg.description}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Panel>
          );
        })}
      </Collapse>

      {/* Summary stats */}
      <div style={{ marginTop: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '4px', display: 'flex', gap: '24px', fontSize: '13px' }}>
        <div>
          <strong>Total Regulations:</strong> {regulations.length}
        </div>
        {Object.entries(grouped).map(([category, regs]) => (
          <div key={category}>
            <strong>{CATEGORY_LABELS[category]?.label || category}:</strong> {regs.length}
          </div>
        ))}
        <div>
          <strong>Critical:</strong> {regulations.filter(r => r.severity === 'critical').length}
        </div>
        <div>
          <strong>High:</strong> {regulations.filter(r => r.severity === 'high').length}
        </div>
      </div>
    </div>
  );
}

export default RegulationDisplay;
